import { ListItemText, Typography } from '@mui/material';
import { GridColDef, GridRenderEditCellParams } from '@mui/x-data-grid-premium';
import { get, isArray, isEmpty, isString } from 'lodash';
import React, { useContext, useState } from 'react';
import { Classification } from 'sr-types/lib/organization/v1/graphql';
import I18n, { useI18n } from '../../common/i18n/I18n';
import LookupGridField from '../../common/list/LookupGridField';
import { useMutation } from '@apollo/client';
import { createTermMutation, metadataClient } from '../../common/components/MetadataAutocomplete';
import { UserContext } from '../../common/auth/UserContext';
import { termCreationExecutor } from '../../supply/termset/termset';
import { useCreateOrganizationWithInput, autoCreateQuery as autoCreateOrganizationQuery,
    client as organizationClient } from '../../organization/organization';
import { useMutationWithContext } from '../../common/hooks/useMutationWithContext';

export default (setIsValid) => {
    const [categoryLineage, setCategoryLineage] = useState(undefined);
    const { activeOrganizationAccount } = useContext(UserContext);
    const [saveOrganization] = useMutationWithContext(autoCreateOrganizationQuery, organizationClient);
    
    const [createTerm] = useMutation(createTermMutation, {
        client: metadataClient,
        context: {
            headers: {
                ownerId: activeOrganizationAccount
            }
        },
        notifyOnNetworkStatusChange: true
    });

    const organizationToUpdate = (colDefs, field, entity) => {
        const orgCategories = get(entity, 'hierarchicalCategories');
        setCategoryLineage(!isEmpty(orgCategories) ? orgCategories[0].lineage : undefined);
        return [
            { field: 'organization', value: get(entity, 'name') },
            { field: 'organizationReference', value: get(entity, 'reference') },
            { field: 'organizationTypes', value: get(entity, 'organizationTypes') },
            { field: 'organizationCategories', value: orgCategories },
            { field: 'productionRoles', value: get(entity, 'productionRoles') }
        ];
    };

    const categoryToUpdate = (colDefs, field, entity) => {
        setCategoryLineage(get(entity, 'lineage'));
        return [{ field: 'organizationCategories', value: get(entity, 'name') }];
    };

    const typeToUpdate = (colDefs, field, entity) => {
        return [{ field: 'organizationTypes', value: get(entity, 'name') }];
    };

    const rolesToUpdate = (colDefs, field, entity) => {
        return [{ field: 'productionRoles', value: get(entity, 'name') }];
    };

    const valueGetter = (value: string) => value;

    const defaultOptionRenderer =
        (prop) =>
        ({ option }) => (
            <Typography className="ellipsis" fontSize={'small'}>
                {get(option, prop)}
            </Typography>
        );

    const categoryRenderer =
        (prop) =>
        ({ option }) => <ListItemText primary={get(option, prop)} secondary={get(option, 'lineage.value')} />;
    const defaultEntityToOption = (entity) => entity;

    const convertTypeToArray = (value, row) => {
        const organizationTypes = value ? [value] : [];
        return { ...row, organizationTypes };
    };

    const convertProductionRoleToArray = (value, row) => {
        const productionRoles = value ? [value] : [];
        return { ...row, productionRoles };
    };

    const typesValueGetter = (params) => {
        return params && !isEmpty(params) ? params[0] : undefined;
    };

    const parseTypes = (value) => {
        return isArray(value) && !isEmpty(value) ? value[0] : value;
    };

    const convertCategoryToObjectArray = (value, row) => {
        const organizationCategories: Classification[] = value ? [{ value, lineage: categoryLineage ?? row?.organizationCategories?.[0]?.lineage }] : [];
        return { ...row, organizationCategories };
    };

    const categoriesValueGetter = (params) => {
        return params && !isEmpty(params[0]?.value) && !isEmpty(organizationCategories) ? params[0].value : undefined;
    };

    const parseOrganization = (value) => {
        return isArray(value) && !isEmpty(value) ? value[0].value : isString(value) ? value : undefined;
    };

    const createOrg = useCreateOrganizationWithInput(saveOrganization)

    const name: GridColDef = {
        field: 'organization',
        headerName: useI18n('production.tabs.associatedOrgs.organization.name'),
        flex: 1,
        sortable: true,
        editable: true,
        valueGetter: valueGetter,
        renderEditCell: (params: GridRenderEditCellParams) => {
            return (
                <LookupGridField
                    {...params}
                    columns={colDefs}
                    filters={[{ identifier: 'entity', value: 'Organization' }]}
                    OptionRenderer={defaultOptionRenderer('name')}
                    toOption={defaultEntityToOption}
                    entityToUpdate={organizationToUpdate}
                    filter={{ expressions: [{ field: 'inactive', value: { values: ['false'] } }] }}
                    afterSearch={(items, term, currentEditRows, id) => {
                        const orgRef = currentEditRows[id]?.organizationReference?.value;
                        if (!orgRef && !orgRef?.id && term) {
                            return <I18n token="production.tabs.associatedOrgs.valMsg.org.notSelected" />;
                        } else if (!orgRef && !orgRef?.id) {
                            return <I18n token="production.tabs.associatedOrgs.valMsg.org.required" />;
                        }
                    }}
                    autoCreate
                    onAutoCreate={createOrg}
                />
            );
        }
    };

    const organizationCategories: GridColDef = {
        field: 'organizationCategories',
        headerName: useI18n('production.tabs.associatedOrgs.organization.category'),
        flex: 1,
        sortable: true,
        editable: true,
        valueGetter: categoriesValueGetter,
        valueSetter: convertCategoryToObjectArray,
        valueParser: parseOrganization,
        renderEditCell: (params: GridRenderEditCellParams) => {
            return (
                <LookupGridField
                    {...params}
                    columns={colDefs}
                    filters={[{ identifier: 'entity', value: 'Category' }]}
                    OptionRenderer={categoryRenderer('name')}
                    toOption={defaultEntityToOption}
                    entityToUpdate={categoryToUpdate}
                />
            );
        }
    };

    const organizationTypes: GridColDef = {
        field: 'organizationTypes',
        headerName: useI18n('production.tabs.associatedOrgs.organization.type'),
        flex: 1,
        sortable: true,
        editable: true,
        valueGetter: typesValueGetter,
        valueSetter: convertTypeToArray,
        valueParser: parseTypes,
        renderEditCell: (params: GridRenderEditCellParams) => {
            return (
                <LookupGridField
                    {...params}
                    columns={colDefs}
                    filters={[{ identifier: 'entity', value: 'OrganizationType' }]}
                    OptionRenderer={defaultOptionRenderer('name')}
                    toOption={defaultEntityToOption}
                    entityToUpdate={typeToUpdate}
                />
            );
        }
    };

    const organizationReference: GridColDef = {
        field: 'organizationReference',
        flex: 1,
        sortable: true,
        editable: true,
        hideable: false,
        valueGetter: valueGetter,
        valueSetter: (value, row) => {
            row['organizationReference'] = value;
            return row;
        },
        preProcessEditCellProps: ({ props }) => {
            const isValid = !!props?.value?.id;
            setIsValid(isValid);
            return { ...props };
        }
    };

    const productionRoles: GridColDef = {
        field: 'productionRoles',
        headerName: useI18n('production.tabs.associatedOrgs.production.roles'),
        flex: 1,
        sortable: true,
        editable: true,
        valueGetter: typesValueGetter,
        valueSetter: convertProductionRoleToArray,
        renderEditCell: (params: GridRenderEditCellParams) => {
            return (
                <LookupGridField
                    {...params}
                    columns={colDefs}
                    filters={[{ identifier: 'entity', value: 'OrganizationRole' }]}
                    OptionRenderer={defaultOptionRenderer('name')}
                    toOption={defaultEntityToOption}
                    entityToUpdate={rolesToUpdate}
                    autoCreate
                    onAutoCreate= {termCreationExecutor(createTerm)}
                />
            );
        }
    };

    const colDefs: GridColDef[] = [name, organizationTypes, organizationCategories, organizationReference, productionRoles];
    return colDefs;
};

export const columnVisibility = {
    organizationReference: false
};
